import { HttpErrorResponse } from '@angular/common/http';

// Sözleşme v1.1 §7 + API OrgExceptions.cs (OrgErrors.All, TicketUsed) ve uçlarda kullanılan ek kodlar
// (QuotaExceeded, PartnerNotReady, InvalidEnvelope, InvalidInput). Bilinmeyen kod 'Unknown' olur.
export type OrgErrorCode =
  | 'TokenExpired' | 'DeviceRevoked' | 'NotOrgHolder' | 'WrongCode' | 'WrongScope' | 'NotAdmin' | 'LoginExpired'
  | 'Closed' | 'TicketUsed' | 'Conflict' | 'ChannelDirty' | 'ApprovalRequired' | 'FormInUse' | 'SlugTaken'
  | 'OneEntryType' | 'TypeInUse' | 'RecoveryNotApproved' | 'NoPqKey' | 'BadCursor' | 'StaleKeyVersion' | 'MemberNoKey' | 'RotationPlanStale' | 'RotationRootMismatch' | 'BucketKeyMismatch'
  | 'QuotaExceeded' | 'PartnerNotReady' | 'InvalidEnvelope' | 'InvalidInput'
  // 2026-10-22: 403, üyenin türünde mesajlaşma kapalı (konuşma açma / gönderme).
  | 'MessagingDisabled'
  // 2026-10-29: DELETE /v2/Org/KeyHolders/{userId} (yetkili çıkarma): 404 HolderNotFound, 409 CannotRemoveSelf / LastHolder
  // (403 NotOrgHolder yukarıda). QR ile yetkili daveti: 410 InviteExpired, 409 InviteTaken / InviteLimit / InviteNotAccepted,
  // 404 InviteNotFound; AlreadyHolder sunucuda 409 Conflict + reason olarak da gelebilir (bkz. reason).
  | 'HolderNotFound' | 'LastHolder' | 'CannotRemoveSelf'
  | 'InviteExpired' | 'InviteTaken' | 'InviteLimit' | 'InviteNotFound' | 'InviteNotAccepted' | 'AlreadyHolder';

export interface OrgError {
  status: number; code: OrgErrorCode | 'Unknown'; approvalId?: string;
  /** 409 QuotaExceeded: sınır ve kullanılan (kota metni için). */
  limit?: number; used?: number;
  /** 409 Conflict ayrıntısı (ConflictReasonException, ör. AlreadyHolder). */
  reason?: string;
  raw: unknown;
}

const KNOWN: ReadonlySet<string> = new Set<OrgErrorCode>([
  'TokenExpired', 'DeviceRevoked', 'NotOrgHolder', 'WrongCode', 'WrongScope', 'NotAdmin', 'LoginExpired',
  'Closed', 'TicketUsed', 'Conflict', 'ChannelDirty', 'ApprovalRequired', 'FormInUse', 'SlugTaken',
  'OneEntryType', 'TypeInUse', 'RecoveryNotApproved', 'NoPqKey', 'BadCursor', 'StaleKeyVersion', 'MemberNoKey', 'RotationPlanStale', 'RotationRootMismatch', 'BucketKeyMismatch',
  'QuotaExceeded', 'PartnerNotReady', 'InvalidEnvelope', 'InvalidInput', 'MessagingDisabled',
  'HolderNotFound', 'LastHolder', 'CannotRemoveSelf',
  'InviteExpired', 'InviteTaken', 'InviteLimit', 'InviteNotFound', 'InviteNotAccepted', 'AlreadyHolder',
]);

export function parseOrgError(e: unknown): OrgError {
  if (!(e instanceof HttpErrorResponse)) return { status: 0, code: 'Unknown', raw: e };
  const body: Record<string, unknown> = typeof e.error === 'object' && e.error !== null ? (e.error as Record<string, unknown>) : {};
  // Sunucu gövdesi PascalCase olabilir; alan adları harf duyarsız okunur.
  const field = (name: string): unknown => {
    const key = Object.keys(body).find((k) => k.toLowerCase() === name.toLowerCase());
    return key === undefined ? undefined : body[key];
  };
  const rawCode = field('code');
  const code = typeof rawCode === 'string' && KNOWN.has(rawCode) ? (rawCode as OrgErrorCode) : 'Unknown';
  const rawApproval = field('approvalId');
  const out: OrgError = { status: e.status, code, raw: e.error };
  if (typeof rawApproval === 'string') out.approvalId = rawApproval;
  const limit = field('limit');
  const used = field('used');
  if (typeof limit === 'number') out.limit = limit;
  if (typeof used === 'number') out.used = used;
  const reason = field('reason');
  if (typeof reason === 'string') out.reason = reason;
  return out;
}

/**
 * Döndürme yolundaki kodların kullanıcı metni (i18n anahtarı, ORG.ERRORS). Sunucu kodu (parseOrgError) ya da ChannelOpsService'in
 * attığı Error mesajı okunur. Yerel 'OrgKeyOutdated': bu bilgisayardaki kurum anahtarı sunucudakinden eski (QR ile yeniden giriş;
 * RotationKeyElsewhere ile karıştırılmaz). 409 StaleKeyVersion ayrıdır: üyenin anahtarı güncellendi, yeniden deneme çözer.
 * Eşleşme yoksa null (çağıran genel metni gösterir).
 */
const TEXT_KEYS: Readonly<Record<string, string>> = {
  RotationRootMismatch: 'ORG.ERRORS.ROTATION_ROOT_MISMATCH',
  BucketKeyMismatch: 'ORG.ERRORS.BUCKET_KEY_MISMATCH',
  StaleKeyVersion: 'ORG.ERRORS.STALE_KEY_VERSION',
  OrgKeyOutdated: 'ORG.ERRORS.ORG_KEY_OUTDATED',
  RotationKeyElsewhere: 'ORG.ERRORS.ROTATION_KEY_ELSEWHERE',
};

export function orgErrorTextKey(e: unknown): string | null {
  const code = e instanceof HttpErrorResponse ? parseOrgError(e).code : e instanceof Error ? e.message : '';
  return TEXT_KEYS[code] ?? null;
}
